/**
 * Recovery for a stuck zoom.
 *
 * The viewport meta pins scale at 1x, which is enough on Android and in an iOS
 * standalone install. iOS Safari deliberately ignores `user-scalable=no` in a
 * browser tab, though, so a pinch there can still leave the layout wedged —
 * rewriting the meta is the only lever that clears an already-applied zoom.
 */

/** Must stay in step with the viewport meta in index.html. */
const PINNED =
  'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
/** Same thing minus the pin — only ever set for the frame that forces a reset. */
const BASE = 'width=device-width, initial-scale=1, viewport-fit=cover';

function meta(): HTMLMetaElement | null {
  return document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
}

/**
 * Drops any zoom the page is currently sitting at. The engine only re-evaluates
 * the viewport when the value actually changes, and two assignments in the same
 * task can be coalesced into no change at all — hence the frame in between.
 */
export function resetZoom() {
  const tag = meta();
  if (!tag) return;
  tag.content = BASE;
  requestAnimationFrame(() => {
    tag.content = PINNED;
  });
}

export function setUpZoomHandling() {
  // Returning from the app switcher can restore a zoomed viewport, and by then
  // the pinned value is already in the tag — so it needs an explicit reset.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resetZoom();
  });
}
